import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import RouteMarker from "@/components/RouteMarker";
import Seo from "@/components/Seo";

const CITY_GUIDES = [
  { to: "/merida", name: "Mérida", note: "Our home base · primary focus", primary: true },
  { to: "/mexico-city", name: "Mexico City", note: "Big-city energy & amenities" },
  { to: "/guadalajara", name: "Guadalajara", note: "Culture, comfort & expat scene" },
  { to: "/queretaro", name: "Querétaro", note: "Safe, modern & easy to love" },
  { to: "/puerto-vallarta", name: "Puerto Vallarta", note: "Beach life with amenities" },
  { to: "/san-miguel-de-allende", name: "San Miguel de Allende", note: "Art, charm & community" },
  { to: "/oaxaca-city", name: "Oaxaca City", note: "Culture, cuisine & color" },
  { to: "/monterrey", name: "Monterrey", note: "Modern & business-forward" },
];

export default function ExploreMexico() {
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/destinations").then((res) => setDestinations(res.data));
  }, []);

  const primary = destinations.find((d) => d.is_primary);
  const others = destinations.filter((d) => !d.is_primary && d.type !== "route");

  return (
    <div data-testid="page-explore-mexico">
      <Seo
        title="Explore Mexico — Cities & Regions for Your Move"
        description="From Mérida to Oaxaca, San Miguel de Allende, and the coasts — explore popular Mexican destinations for US movers and how each compares for your relocation."
        path="/explore-mexico"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-sand-200">
        <div className="absolute inset-0 pattern-talavera opacity-[0.05]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <RouteMarker className="mb-5" label="Mérida-based · destinations to explore" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              Explore Mexico.
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">
              We're based in Mérida, Yucatán — our home and primary destination focus. But every move
              is personal, so here are other regions we can help you explore and compare based on
              your goals, lifestyle, and budget.
            </p>
          </div>
        </div>
      </section>

      {/* Featured: Mérida */}
      {primary && (
        <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24">
          <div data-testid="destination-primary" className="grid gap-0 overflow-hidden rounded-3xl border border-sand-300 shadow-[0_8px_32px_rgba(42,38,36,0.06)] lg:grid-cols-2">
            <img src={primary.image} alt={primary.name} className="h-72 lg:h-full w-full object-cover" />
            <div className="bg-white p-8 lg:p-12">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta-light px-3 py-1 text-xs uppercase tracking-[0.15em] text-terracotta-700 font-semibold">
                <MapPin size={13} strokeWidth={2} /> {primary.region}
              </span>
              <h2 className="mt-4 font-serif text-3xl text-charcoal-800">{primary.name}</h2>
              <p className="mt-3 text-charcoal-600 leading-relaxed text-lg">{primary.blurb}</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/merida" data-testid="primary-explore-cta" className="rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
                  Explore Mérida
                </Link>
                <Link to="/book" className="rounded-full border border-charcoal-300 px-7 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
                  Book an intro call
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other destinations */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800">Other locations to explore &amp; discuss</h2>
          <p className="mt-3 text-charcoal-600">
            These aren't presented as equal alternatives by default — they're options worth
            discussing depending on your move. We'll help you compare any of them with Mérida.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {others.map((d) => (
            <figure
              key={d.id}
              data-testid={`destination-${d.id}`}
              className="group flex flex-col overflow-hidden rounded-3xl border border-sand-300 bg-white shadow-[0_8px_32px_rgba(42,38,36,0.06)] transition-transform hover:-translate-y-1"
            >
              <div className="overflow-hidden">
                <img
                  src={d.image}
                  alt={d.name}
                  className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="flex flex-1 flex-col p-7">
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-clay-700 font-medium">
                  <MapPin size={13} strokeWidth={2} /> {d.region}
                </span>
                <h3 className="mt-3 font-serif text-2xl text-charcoal-800">{d.name}</h3>
                <p className="mt-2 flex-1 text-charcoal-600 leading-relaxed">{d.blurb}</p>
                <p className="mt-4 text-sm text-charcoal-500 italic">Worth discussing depending on your move goals.</p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium">
                  <Link to="/merida" className="text-terracotta hover:text-terracotta-hover">Compare with Mérida</Link>
                  <span className="text-charcoal-300">·</span>
                  <Link to="/contact" className="text-sage-700 hover:text-sage">Talk to us</Link>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* City guides */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-4" data-testid="city-guides">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800">City guides</h2>
          <p className="mt-3 text-charcoal-600">
            Real, practical guides to popular destinations. Mérida is our home base and primary
            focus — the rest are options worth discussing depending on your goals.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CITY_GUIDES.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`city-guide-link${c.to.replace(/\//g, "-")}`}
              className={`group flex flex-col justify-between rounded-2xl border p-5 transition-all hover:-translate-y-1 ${
                c.primary
                  ? "border-terracotta/40 bg-terracotta-light/40"
                  : "border-sand-300 bg-white hover:shadow-[0_8px_28px_rgba(42,38,36,0.08)]"
              }`}
            >
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-clay-700 font-medium">
                  <MapPin size={12} strokeWidth={2} /> {c.primary ? "Home base" : "Guide"}
                </span>
                <h3 className="mt-2 font-serif text-xl text-charcoal-800">{c.name}</h3>
                <p className="mt-1 text-sm text-charcoal-500">{c.note}</p>
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-terracotta group-hover:gap-2 transition-all">
                Read guide <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-8">
        <div className="rounded-3xl bg-sage-light p-10 lg:p-16 text-center">
          <h2 className="text-3xl font-normal text-charcoal-800">Not sure where to land?</h2>
          <p className="mt-3 text-charcoal-600 max-w-xl mx-auto">
            Many of our clients choose Mérida — but the right fit depends on your lifestyle, budget,
            pets, and driving route. Take the Start Here quiz or book a call and we'll help you decide.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/start-here" data-testid="explore-quiz-cta" className="rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              Take the Start Here quiz
            </Link>
            <Link to="/book" data-testid="explore-book-cta" className="rounded-full border border-charcoal-300 px-8 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
              Book an intro call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
